'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../../lib/auth/context';
import { POPULAR_LOCATIONS, LocationPreset } from '../../lib/location/resolver';
import { LocationAutocomplete } from '../quick-calculator/LocationAutocomplete';
import { createBirthProfile, createChart } from '../../lib/api/client';
import { Calendar, Clock, User, Sparkles, AlertCircle, Loader2, LogIn, Compass } from 'lucide-react';
import Link from 'next/link';
import { AyanamsaPreference } from '../../../../packages/types/src/index';

export function BirthDetailsForm() {
  const router = useRouter();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('1990-01-01');
  const [birthTime, setBirthTime] = useState('12:00:00');

  const [locationName, setLocationName] = useState('New Delhi, India');
  const [selectedPreset, setSelectedPreset] = useState<LocationPreset>(POPULAR_LOCATIONS[0]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [latitude, setLatitude] = useState(28.6139);
  const [longitude, setLongitude] = useState(77.2090);
  const [timezone, setTimezone] = useState('Asia/Kolkata');

  const [ayanamsa, setAyanamsa] = useState<AyanamsaPreference>('lahiri');

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (user) {
      import('../../lib/api/client').then(({ getUserPreferences }) => {
        getUserPreferences().then((prefs) => {
          if (prefs && prefs.defaultAyanamsa) {
            setAyanamsa(prefs.defaultAyanamsa);
          }
        }).catch(() => {});
      });
    }
  }, [user]);

  const handleLocationSelect = (preset: LocationPreset) => {
    setSelectedPreset(preset);
    setLocationName(preset.name);
    setLatitude(preset.latitude);
    setLongitude(preset.longitude);
    setTimezone(preset.timezone);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError('Please sign in or create an account to generate and save birth charts.');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!birthDate) {
      setError('Please enter a valid birth date.');
      return;
    }
    if (!birthTime) {
      setError('Please enter a valid birth time.');
      return;
    }

    try {
      setLoading(true);

      setLoadingStep('Creating birth profile...');
      const profile = await createBirthProfile({
        name: name.trim(),
        birthDate,
        birthTime: birthTime.length === 5 ? `${birthTime}:00` : birthTime,
        latitude: parseFloat(latitude.toString()),
        longitude: parseFloat(longitude.toString()),
        timezone,
        locationName: locationName.trim()
      });

      setLoadingStep('Calculating planetary positions & divisional charts via Swiss Ephemeris...');
      const chartRecord = await createChart(profile.id, undefined, ayanamsa);

      setLoadingStep('Finalizing your birth chart...');
      router.push(`/chart/${chartRecord.id}`);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during calculation.');
      setLoading(false);
      setLoadingStep(null);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl">
      <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-4">
        <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-[#5ab4ff]">
          <Sparkles className="w-5 h-5 text-[#5ab4ff]" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-white">Calculate Your Birth Chart</h2>
          <p className="text-xs text-[rgba(220,236,255,0.75)]">Deterministic Vedic calculation using Swiss Ephemeris</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-[#ff9bb0]/30 flex items-start justify-between gap-3 text-[#ff9bb0] text-sm">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-[#ff9bb0] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#ff9bb0]">Action Required</p>
              <p className="mt-0.5 text-xs text-[#ff9bb0]/90">{error}</p>
            </div>
          </div>
          {!user && (
            <Link
              href="/login"
              className="px-3 py-1.5 rounded-full bg-white text-[#03163d] font-semibold text-xs shrink-0 flex items-center gap-1 hover:bg-white/90 shadow-sm"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign In
            </Link>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name Input */}
        <div>
          <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
            Full Name <span className="text-[#5ab4ff]">*</span>
          </label>
          <div className="relative">
            <User className="w-4 h-4 text-[rgba(220,236,255,0.7)] absolute left-3.5 top-4" />
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rahul Sharma"
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 text-white pl-10 pr-4 text-sm placeholder-[rgba(220,236,255,0.7)] focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
            />
          </div>
        </div>

        {/* Date and Time Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
              Birth Date <span className="text-[#5ab4ff]">*</span>
            </label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-[rgba(220,236,255,0.7)] absolute left-3.5 top-4" />
              <input
                type="date"
                required
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 text-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
              Birth Time <span className="text-[#5ab4ff]">*</span>
            </label>
            <div className="relative">
              <Clock className="w-4 h-4 text-[rgba(220,236,255,0.7)] absolute left-3.5 top-4" />
              <input
                type="time"
                step="1"
                required
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 text-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
              />
            </div>
          </div>
        </div>

        {/* Location Input with Autocomplete */}
        <div>
          <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
            Birth Location <span className="text-[#5ab4ff]">*</span>
          </label>
          <LocationAutocomplete
            value={locationName}
            onSelectLocation={handleLocationSelect}
            onClearLocation={() => setSelectedPreset(null as any)}
            confirmedPreset={selectedPreset}
            idPrefix="birth-form-loc"
          />
        </div>

        {/* Ayanamsa Selection */}
        <div>
          <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center gap-1.5">
            <Compass className="w-4 h-4 text-[#5ab4ff] shrink-0" />
            <span>Calculation Ayanamsa System</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'lahiri', title: 'Lahiri', subtitle: 'Standard' },
              { id: 'raman', title: 'BV Raman', subtitle: 'Raman' },
              { id: 'krishnamurti', title: 'KP', subtitle: 'Krishnamurti' }
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setAyanamsa(option.id as AyanamsaPreference)}
                className={`h-12 px-2 sm:px-3 rounded-xl border transition-all cursor-pointer flex flex-col items-center justify-center text-center min-w-0 ${
                  ayanamsa === option.id
                    ? 'bg-white text-[#03163d] border-white font-bold shadow-[0_0_15px_rgba(150,165,255,0.3)]'
                    : 'bg-white/[0.05] border-white/15 text-[rgba(220,236,255,0.85)] hover:bg-white/10 hover:text-white font-medium'
                }`}
              >
                <span className="text-xs leading-tight truncate max-w-full">
                  {option.title}
                </span>
                <span
                  className={`text-[10px] leading-tight truncate max-w-full ${
                    ayanamsa === option.id ? 'opacity-85 font-medium' : 'opacity-70'
                  }`}
                >
                  ({option.subtitle})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Advanced Coordinates Accordion */}
        <div className="border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs text-[rgba(220,236,255,0.75)] hover:text-[#5ab4ff] transition-colors flex items-center gap-1 font-medium cursor-pointer"
          >
            {showAdvanced ? 'Hide Advanced Geographic Details' : 'Show Advanced Geographic Details (Lat/Lon/Timezone)'}
          </button>

          {showAdvanced && (
            <div className="mt-3 p-4 bg-white/[0.04] rounded-xl border border-white/12 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block text-[rgba(220,236,255,0.75)] mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={latitude}
                  onChange={(e) => setLatitude(parseFloat(e.target.value))}
                  className="w-full h-10 rounded-lg bg-white/[0.06] border border-white/20 text-white px-3 text-xs focus:outline-none focus:ring-1 focus:ring-[#5ab4ff]"
                />
              </div>
              <div>
                <label className="block text-[rgba(220,236,255,0.75)] mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={longitude}
                  onChange={(e) => setLongitude(parseFloat(e.target.value))}
                  className="w-full h-10 rounded-lg bg-white/[0.06] border border-white/20 text-white px-3 text-xs focus:outline-none focus:ring-1 focus:ring-[#5ab4ff]"
                />
              </div>
              <div>
                <label className="block text-[rgba(220,236,255,0.75)] mb-1">Timezone</label>
                <input
                  type="text"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full h-10 rounded-lg bg-white/[0.06] border border-white/20 text-white px-3 text-xs focus:outline-none focus:ring-1 focus:ring-[#5ab4ff]"
                />
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-4 h-12 px-8 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-[#03163d]" />
              <span>{loadingStep || 'Calculating...'}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 text-[#03163d]" />
              <span>Generate My Birth Chart</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
